'use client';

import Link from 'next/link';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Password } from '@/components/ui/password';
import { SubmitHandler } from 'react-hook-form';
import { Form } from '@/components/ui/form';
import { Text } from '@/components/ui/text';
import { routes } from '@/config/routes';
import toast from 'react-hot-toast';
import {
  resetPasswordSchema,
  ResetPasswordSchema,
} from '@/utils/validators/reset-password.schema';
import axios from 'axios';
import { getCresivoxBaseUrl } from '@/projectConfig/cresivoxBaseUrl';
import { getRecaptchaToken } from '@/utils/recaptcha';

enum FormStage {
  EMAIL_INPUT,
  OTP_VERIFICATION,
  RESET_PASSWORD
}

const initialValues = {
  email: '',
  password: '',
  confirmPassword: '',
};

export default function ForgetPasswordForm() {
  const router = useRouter();
  const [reset, setReset] = useState({});
  const [formStage, setFormStage] = useState<FormStage>(FormStage.EMAIL_INPUT);
  const [resetToken, setResetToken] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [maskedMobile, setMaskedMobile] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
 

  const API_URL = getCresivoxBaseUrl();

  // Function to handle email submission and fetch associated mobile
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    
    try {
      const recaptchaToken = await getRecaptchaToken("lookup");
      const response = await axios.post(`${API_URL}/associated-mobile`, { email, recaptchaToken });
      
      if (response.data.success) {
        const mobile = response.data.mobile;
        setMobile(mobile);
        
        // Mask the mobile number to show only last 4 digits
        const lastFourDigits = mobile.slice(-4);
        setMaskedMobile(`XX-XXXX-${lastFourDigits}`);
        
        setFormStage(FormStage.OTP_VERIFICATION);
      } else {
        setErrorMessage(response.data.message || 'No account found with this email');
      }
    } catch (error) {
      console.error('Error fetching associated mobile:', error);
      setErrorMessage('Failed to find account with this email');
    }
  };

  // Function to send OTP to the mobile number
  const handleSendOTP = async () => {
    setErrorMessage('');
    setIsVerifying(true);
    
    try {
      const recaptchaToken = await getRecaptchaToken("otp");
      const response = await axios.post(`${API_URL}/sms/send-otp`, { 
        mobile,
        name: email.split('@')[0], // Optional: Use part of email as name
        recaptchaToken
      });
      
      if (response.data.message) {
        setOtpSent(true);
        toast.success("OTP send successfully")
      } else {
        setErrorMessage('Failed to send OTP');
        toast.error("Failed to send OTP")
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      setErrorMessage('Failed to send OTP');
    } finally {
      setIsVerifying(false);
    }
  };

  // Function to handle OTP input changes
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      value = value.slice(0, 1);
    }
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Auto-focus to next input if current input is filled
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  // Function to handle OTP verification
  const handleVerifyOTP = async () => {
    setErrorMessage('');
    setIsVerifying(true);
    
    const otpValue = otp.join('');
    if (otpValue.length !== 6) {
      setErrorMessage('Please enter a valid 6-digit OTP');
      setIsVerifying(false);
      return;
    }
    
    try {
      const recaptchaToken = await getRecaptchaToken("otp");
      const response = await axios.post(`${API_URL}/verify-phone-otp`, { 
        mobile, 
        otp: otpValue,
        recaptchaToken
      });
      
      if (response.data.success) {
        setResetToken(response.data.resetToken || '');
        setFormStage(FormStage.RESET_PASSWORD);
        
      } else {
        setErrorMessage('Invalid or expired OTP');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setErrorMessage('Failed to verify OTP');
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle password reset submission
  const onSubmit: SubmitHandler<ResetPasswordSchema> = async (data) => {
    setIsResetting(true);
   
    setErrorMessage('');
   
    
    try {
      const recaptchaToken = await getRecaptchaToken("password-reset");
      // Call your API to reset the password
      const response = await axios.post(`${API_URL}/reset-password`, {
        email: data.email,
        password: data.password,
        recaptchaToken,
        resetToken
      });
      
      if (response.data.success) {
        // Reset form and redirect to sign in
        setReset(initialValues);
        toast.success('Password reset successfully');
        router.push("/signin");
      } else {
        setErrorMessage(response.data.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setErrorMessage('Failed to reset password');
    } finally {
      setIsResetting(false);
    }
  };

  // Render OTP input fields
  const renderOtpInputs = () => {
    return (
      <div className="flex gap-2 justify-between mb-6">
        {otp.map((digit, index) => (
          <Input
            key={index}
            id={`otp-${index}`}
            type="text"
            inputMode="numeric"
            maxLength={1}
            size="lg"
            className="w-12 text-center"
            value={digit}
            onChange={(e) => handleOtpChange(index, e.target.value)}
            onKeyDown={(e) => {
              // Handle backspace to move to previous input
              if (e.key === 'Backspace' && !digit && index > 0) {
                const prevInput = document.getElementById(`otp-${index - 1}`);
                if (prevInput) {
                  prevInput.focus();
                }
              }
            }}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      {errorMessage && (
        <div className="mb-4 p-2 bg-red-50 text-red-500 rounded-md text-sm">
          {errorMessage}
        </div>
      )}

      {formStage === FormStage.EMAIL_INPUT && (
        <form onSubmit={handleEmailSubmit} className="pt-1.5 space-y-6">
          <Input
            type="text"
            size="lg"
            label="Email/Mobile Number"
            placeholder="Enter your email or mobile number"
            className="[&>label>span]:font-medium"
            color="info"
            inputClassName="text-sm"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Button
            className="mt-2 w-full"
            type="submit"
            size="lg"
            color="info"
          >
            Continue
          </Button>
        </form>
      )}

      {formStage === FormStage.OTP_VERIFICATION && (
        <div className="pt-1.5 space-y-6">
          <div className="text-center mb-4">
            <Text className="text-gray-500">
              We found a mobile number associated with your account:
            </Text>
            <Text className="font-semibold text-gray-700 mt-2">
              {maskedMobile}
            </Text>
          </div>

          {!otpSent ? (
            <Button
              className="mt-2 w-full"
              size="lg"
              color="info"
              onClick={handleSendOTP}
              disabled={isVerifying}
            >
              {isVerifying ? 'Sending...' : 'Send OTP'}
            </Button>
          ) : (
            <>
              <Text className="text-center text-gray-500">
                Enter the 6-digit OTP sent to your mobile
              </Text>
              {renderOtpInputs()}
              <Button
                className="mt-2 w-full"
                size="lg"
                color="info"
                onClick={handleVerifyOTP}
                disabled={isVerifying || otp.join('').length !== 6}
              >
                {isVerifying ? 'Verifying...' : 'Verify OTP'}
              </Button>
              <div className="text-center mt-4">
                <Button
                  variant="text"
                  color="info"
                  onClick={handleSendOTP}
                  disabled={isVerifying}
                >
                  Resend OTP
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {formStage === FormStage.RESET_PASSWORD && (
        <>
          <Form<ResetPasswordSchema>
            validationSchema={resetPasswordSchema}
            resetValues={reset}
            onSubmit={onSubmit}
            useFormProps={{
              mode: 'onChange',
              defaultValues: { ...initialValues, email },
            }}
            className="pt-1.5"
          >
            {({ register, formState: { errors } }) => (
              <div className="space-y-6">
                <Input
                  type="text"
                  size="lg"
                  label="Email/Mobile Number"
                  className="[&>label>span]:font-medium"
                  color="info"
                  inputClassName="text-sm"
                  {...register('email')}
                  error={errors.email?.message}
                  readOnly
                />
                <Password
                  label="New Password"
                  placeholder="Enter your new password"
                  size="lg"
                  className="[&>label>span]:font-medium"
                  color="info"
                  inputClassName="text-sm"
                  {...register('password')}
                  error={errors.password?.message}
                />
                <Password
                  label="Confirm Password"
                  placeholder="Confirm your new password"
                  size="lg"
                  className="[&>label>span]:font-medium"
                  color="info"
                  inputClassName="text-sm"
                  {...register('confirmPassword')}
                  error={errors.confirmPassword?.message}
                />
                <Button
                  className="mt-2 w-full"
                  type="submit"
                  size="lg"
                  color="info"
                  disabled={isResetting}
                >
                  {isResetting ? 'Resetting...' : 'Reset Password'}
                </Button>
              </div>
            )}
          </Form>
        </>
      )}

      <Text className="mt-6 text-center text-[15px] leading-loose text-gray-500 lg:mt-8 lg:text-start xl:text-base">
        Don't want to reset your password?{' '}
        <Link
          href={routes.signIn}
          className="font-bold text-gray-700 transition-colors hover:text-blue"
        >
          Sign In
        </Link>
      </Text>
    </>
  );
}
